import { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Container,
    Divider,
    Link,
    TextField,
    Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { sendPasswordRedefinitionMail } from "../../../app/features/sessions/sessionSlice";
import { AppDispatch, RootState } from "../../../app/store";
import * as REQUEST_REQUIREMENTS from '../../../app/api/requestRequirements';
import jinnerLogo from '../../../app/assets/logo-white-name-white-without-background-portrait.png';

const BG = '#15151a';
const CARD_BG = '#1c1c24';
const BRAND_RED = '#c0392b';
const TEXT = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.6)';
const BORDER = 'rgba(255,255,255,0.12)';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        color: TEXT,
        '& fieldset': { borderColor: BORDER },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },

        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px #1c1c24 inset`,
            WebkitTextFillColor: '#f4f4f6',
            caretColor: '#f4f4f6',
        },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
    },
    '& .MuiInputLabel-root': { color: TEXT_SECONDARY },
    '& .MuiInputLabel-root.Mui-focused': { color: '#f4f4f6', fontWeight: 700 },
};

export function ResetPassword() {
    const [email, setEmail] = useState('');
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState('');
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const loading = useSelector((state: RootState) => state.session.loading);

    async function handleSendEmail() {
        if (!email.trim()) {
            setErrorMessages(['Por favor insere o teu e-mail.']);
            return;
        }

        setErrorMessages([]);
        setSuccessMessage('');

        const response = await dispatch(sendPasswordRedefinitionMail({
            email,
            redirectUrl: REQUEST_REQUIREMENTS.REDIRECTION_PASSWORD_PAGE_ENDPOINT,
        }));

        const payload = response.payload as any;

        if (payload?.errors) {
            setSuccessMessage('');
            setErrorMessages(payload.errors.full_messages ?? payload.errors ?? ['Ocorreu um erro.']);
        } else if (payload?.success === true) {
            setEmail('');
            setErrorMessages([]);
            setSuccessMessage(payload.message ?? 'E-mail de recuperação enviado com sucesso.');
        } else {
            setErrorMessages(['Ocorreu um erro. Tenta novamente.']);
        }
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BG, display: 'flex', alignItems: 'center' }}>
            <Container maxWidth="sm">
                <Card sx={{
                    bgcolor: CARD_BG,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                }}>
                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Box component="img" src={jinnerLogo} alt="Jinner" sx={{ height: 80 }} />
                        </Box>

                        <Typography variant="h5" fontWeight={700} color={TEXT} sx={{ mb: 3 }}>
                            Reset Password
                        </Typography>

                        {errorMessages.length > 0 && (
                            <Alert severity="error" sx={{ mb: 2 }} aria-live="assertive">
                                {errorMessages.map((e, i) => <div key={i}>{e}</div>)}
                            </Alert>
                        )}

                        {successMessage && (
                            <Alert severity="success" sx={{ mb: 2 }} aria-live="assertive">
                                {successMessage}
                            </Alert>
                        )}

                        <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && !loading && handleSendEmail()}
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            sx={fieldSx}
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            onClick={handleSendEmail}
                            sx={{
                                mt: 3,
                                py: 1.4,
                                bgcolor: BRAND_RED,
                                '&:hover': { bgcolor: '#a93226' },
                                '&.Mui-disabled': { bgcolor: 'rgba(192,57,43,0.4)' },
                            }}
                        >
                            {loading ? <CircularProgress size={22} color="inherit" /> : 'Send recovery email'}
                        </Button>
                    </CardContent>

                    <Divider sx={{ borderColor: BORDER }} />

                    <CardActions sx={{ justifyContent: 'center', py: 2 }}>
                        <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                            Remember your password?{' '}
                            <Link
                                component="button"
                                onClick={() => navigate(REQUEST_REQUIREMENTS.SIGNIN_ENDPOINT)}
                                sx={{ color: BRAND_RED, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                                Sign in
                            </Link>
                        </Typography>
                    </CardActions>
                </Card>
            </Container>
        </Box>
    );
}
